/**
 * What a screen of a feature above the plan says about the plan and who can
 * act on it — one place for `TierLockedScreen` and the banner over demo data
 * (#2816), so the two never disagree about the CTA:
 *
 * - the plan from the catalog gate (`minPlanLabel`), never written next to the
 *   flag key;
 * - the plans link only for a role that can open billing (anyone else would
 *   land on settings without it). Outside the permission context (the
 *   Bitrix24 iframe, where there is no payment, and tests) there is no link.
 */
import type { FeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useOptionalPermissions } from '@/contexts/PermissionContext';
import { IS_EMBEDDED_SESSION } from '@/lib/embedded-session';
import { minPlanLabel } from '@/lib/product-package-label';
import { canAccessScreen } from '@/lib/route-permissions';

export interface TierPlanCta {
  /** The plan the feature is sold from, or `null` for a flag outside the catalog gates. */
  plan: string | null;
  /** The role may open the plans: show the link, not «ask the owner». */
  canOpenPlans: boolean;
  /** The Bitrix24 iframe: the owner changes the plan in the web app. */
  embedded: boolean;
}

export function useTierPlanCta(flag: keyof FeatureFlags): TierPlanCta {
  return { plan: minPlanLabel(flag), ...usePlansCta() };
}

/**
 * Who can act on the plan, without a feature — for the Pro trial banner
 * (#2816), which speaks of every Pro feature at once. The same rule as above.
 */
export function usePlansCta(): Omit<TierPlanCta, 'plan'> {
  const permissions = useOptionalPermissions();
  return {
    canOpenPlans:
      !!permissions &&
      canAccessScreen('billing', permissions.globalRole, permissions.isOwner),
    embedded: IS_EMBEDDED_SESSION,
  };
}
